/**
 * Absatzweiser Zugriff auf den Text einer Form.
 *
 * Die Momentaufnahme in `presentation.ts` liest den Text eines Platzhalters am
 * Stück – das reicht für fast alles. Für die Gliederungsebenen reicht es nicht:
 * `ParagraphFormat` gehört immer zu einem Textbereich, und der Bereich über den
 * gesamten Textrahmen liefert bei gemischten Ebenen nur `null`.
 *
 * Deshalb wird hier für jeden Absatz ein eigener Teilbereich angefordert. Das
 * kostet einen zusätzlichen Abruf und setzt `ParagraphFormat.indentLevel`
 * voraus, das es erst ab PowerPointApi 1.10 gibt.
 */

import { powerPointApi, supportsPowerPointApi } from "./capabilities";
import { isPowerPointAvailable, PowerPointUnavailableError } from "./presentation";

export interface ParagraphLevelInfo {
  slideIndex: number;
  shapeId: string;
  /** Nullbasierte Nummer des Absatzes innerhalb der Form. */
  paragraphIndex: number;
  text: string;
  /** Gliederungsebene, 1 = oberste Ebene. */
  level: number | null;
}

/** Formarten, die einen Textrahmen haben – siehe presentation.ts. */
const TEXT_SHAPE_TYPES = new Set(["Placeholder", "TextBox", "GeometricShape", "Callout", "Freeform"]);

interface Absatz {
  start: number;
  laenge: number;
  text: string;
}

/**
 * Zerlegt den Text eines Rahmens in Absätze und merkt sich die Zeichenposition.
 *
 * Die Position wird am Rohtext abgezählt statt berechnet – so bleibt sie auch
 * dann richtig, wenn PowerPoint Absätze mit "\r", "\n" oder "\r\n" trennt.
 */
export function absaetzeMitPosition(text: string): Absatz[] {
  const absaetze: Absatz[] = [];
  let start = 0;

  for (let i = 0; i <= text.length; i += 1) {
    const istTrenner = i === text.length || /[\r\n\v]/.test(text[i]);
    if (!istTrenner) continue;

    const roh = text.slice(start, i);
    if (roh.trim().length > 0) {
      absaetze.push({ start, laenge: roh.length, text: roh.trim() });
    }
    start = i + 1;
  }
  return absaetze;
}

/**
 * Gliederungsebene jedes Absatzes aller Textformen.
 * `null`, wenn PowerPointApi 1.10 fehlt.
 */
export async function readParagraphLevels(): Promise<ParagraphLevelInfo[] | null> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();
  if (!supportsPowerPointApi(powerPointApi.indentLevel)) return null;

  try {
    return await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();

      const shapeCollections = slides.items.map((slide) => {
        const shapes = slide.shapes;
        shapes.load("items/id,items/type");
        return shapes;
      });
      await context.sync();

      // Erst den vollständigen Text holen – ohne ihn sind die Absatzgrenzen
      // und damit die Teilbereiche nicht bekannt.
      const rahmen: Array<{
        slideIndex: number;
        shapeId: string;
        frame: PowerPoint.TextFrame;
      }> = [];
      shapeCollections.forEach((shapes, slideIndex) => {
        for (const shape of shapes.items) {
          if (typeof shape.type === "string" && !TEXT_SHAPE_TYPES.has(shape.type)) continue;
          const frame = shape.textFrame;
          frame.textRange.load("text");
          rahmen.push({
            slideIndex,
            shapeId: typeof shape.id === "string" ? shape.id : "",
            frame,
          });
        }
      });
      if (rahmen.length === 0) return [];
      await context.sync();

      const anfragen: Array<{
        slideIndex: number;
        shapeId: string;
        paragraphIndex: number;
        text: string;
        format: PowerPoint.ParagraphFormat;
      }> = [];

      for (const eintrag of rahmen) {
        let text = "";
        try {
          text = typeof eintrag.frame.textRange.text === "string"
            ? eintrag.frame.textRange.text
            : "";
        } catch {
          continue;
        }

        absaetzeMitPosition(text).forEach((absatz, paragraphIndex) => {
          const bereich = eintrag.frame.textRange.getSubstring(absatz.start, absatz.laenge);
          const format = bereich.paragraphFormat;
          format.load("indentLevel");
          anfragen.push({
            slideIndex: eintrag.slideIndex,
            shapeId: eintrag.shapeId,
            paragraphIndex,
            text: absatz.text,
            format,
          });
        });
      }
      if (anfragen.length === 0) return [];
      await context.sync();

      return anfragen.map((anfrage) => {
        let level: number | null = null;
        try {
          level =
            typeof anfrage.format.indentLevel === "number" ? anfrage.format.indentLevel : null;
        } catch {
          level = null;
        }
        return {
          slideIndex: anfrage.slideIndex,
          shapeId: anfrage.shapeId,
          paragraphIndex: anfrage.paragraphIndex,
          text: anfrage.text,
          level,
        };
      });
    });
  } catch {
    return null;
  }
}

/** Alle in einer Form vorkommenden Gliederungsebenen, aufsteigend sortiert. */
export function levelsOfShape(levels: ParagraphLevelInfo[], shapeId: string): number[] {
  const gefunden = new Set<number>();
  for (const eintrag of levels) {
    if (eintrag.shapeId === shapeId && eintrag.level !== null) gefunden.add(eintrag.level);
  }
  return [...gefunden].sort((a, b) => a - b);
}
